import { localStorage } from "dash-ui";
import Cookies from "js-cookie";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useRecordLastTouchAttributionMutation } from "gql/generated.graphql";
import {
  AttributionPayload,
  FIRST_ATTR_COOKIE,
  LAST_ATTR_COOKIE,
  TRACKED_PARAMS,
  readAttributionCookie,
  sanitizeAttribution,
} from "shared/attribution";
import { parentDomainCookieOptions } from "shared/cookies";

const FIRST_ATTR_EXPIRY_DAYS = 730;
const LAST_ATTR_EXPIRY_DAYS = 90;

export {
  FIRST_ATTR_COOKIE,
  LAST_ATTR_COOKIE,
  readLastAttribution,
} from "shared/attribution";
export type { AttributionPayload } from "shared/attribution";

const buildPayload = (search: string): AttributionPayload | null => {
  const params = new URLSearchParams(search);
  const payload: AttributionPayload = { captured_at: new Date().toISOString() };
  let hasMarketingParam = false;

  for (const key of TRACKED_PARAMS) {
    const value = params.get(key);
    if (value) {
      payload[key] = value;
      hasMarketingParam = true;
    }
  }

  if (!hasMarketingParam) return null;

  if (document.referrer) payload.referrer = document.referrer;

  return payload;
};

export const useAttributionCapture = () => {
  const location = useLocation();
  const [recordLastTouch] = useRecordLastTouchAttributionMutation();

  useEffect(() => {
    const payload = buildPayload(location.search);
    if (!payload) return;

    const serialized = JSON.stringify(payload);

    Cookies.set(
      LAST_ATTR_COOKIE,
      serialized,
      parentDomainCookieOptions(LAST_ATTR_EXPIRY_DAYS)
    );

    if (!Cookies.get(FIRST_ATTR_COOKIE)) {
      Cookies.set(
        FIRST_ATTR_COOKIE,
        serialized,
        parentDomainCookieOptions(FIRST_ATTR_EXPIRY_DAYS)
      );
    }

    // Sync this latest touch to Zoho as the contact's Last Touch, but only for
    // authenticated users — for logged-out visitors the touch is captured at
    // signup/subscription instead, and there's no contact to attach it to yet.
    const { captured_at: _captured_at, ...attribution } = payload;
    if (localStorage.getAccessToken() && Object.keys(attribution).length > 0) {
      recordLastTouch({ variables: { attribution } }).catch(() => {
        // Best-effort marketing attribution — never disrupt the session on failure.
      });
    }
  }, [location.search, recordLastTouch]);
};

// Gated by REACT_APP_DDA_ENABLED: dashtodash.com (landing) and the app may not
// share a parent domain, so the cookies the WordPress tracker writes on
// .dashtodash.com can be invisible to the app. The plugin exposes a REST
// endpoint that returns the touch state for the visitor; calling it with
// credentials: 'include' makes the browser send those cross-site cookies, and
// the response is merged with whatever the in-app hook captured from URL
// params. Where the landing and the app share a parent domain (cookies flow
// directly), leave the flag unset and this code path is a no-op.
const DDA_ENDPOINT = "https://dashtodash.com/wp-json/dda/v1/touch";

const stripExtraTouchFields = (
  touch: Record<string, unknown> | undefined
): Omit<AttributionPayload, "captured_at"> | undefined => {
  if (!touch) return undefined;
  const { landing_page, timestamp, captured_at, ...rest } =
    touch as AttributionPayload & {
      landing_page?: string;
      timestamp?: string;
    };
  return rest;
};

const fetchLandingAttribution = async (): Promise<{
  first?: Omit<AttributionPayload, "captured_at">;
  signup?: Omit<AttributionPayload, "captured_at">;
}> => {
  if (process.env["REACT_APP_DDA_ENABLED"] !== "true") return {};

  try {
    const secret = process.env["REACT_APP_DDA_SECRET"];
    const headers: Record<string, string> = secret
      ? { "X-DDA-Secret": secret }
      : {};
    const res = await fetch(DDA_ENDPOINT, { credentials: "include", headers });
    if (!res.ok) return {};
    const data = await res.json();
    if (!data?.has_attribution) return {};
    return {
      first: stripExtraTouchFields(data.first_touch),
      signup: stripExtraTouchFields(data.last_touch),
    };
  } catch {
    return {};
  }
};

export const readSignupAttribution = async () => {
  const local = {
    first: readAttributionCookie(FIRST_ATTR_COOKIE),
    signup: readAttributionCookie(LAST_ATTR_COOKIE),
  };

  const landing = await fetchLandingAttribution();

  return {
    first: sanitizeAttribution(landing.first ?? local.first),
    signup: sanitizeAttribution(landing.signup ?? local.signup),
  };
};

// SSO bounces redirect the browser to the backend and back, and the
// .dashtodash.com cookies never travel on those backend requests. Mirroring the
// landing values into this app's own cookies BEFORE the redirect lets the SSO
// strategies pick them up via readSignupAttributionFromRequest() with no
// backend changes. When REACT_APP_DDA_ENABLED is unset this is a no-op
// (fetchLandingAttribution gates itself). Awaiting this is fine — the user
// already expects a redirect.
export const seedLandingAttributionCookies = async (): Promise<void> => {
  const landing = await fetchLandingAttribution();
  const now = new Date().toISOString();

  if (landing.first) {
    Cookies.set(
      FIRST_ATTR_COOKIE,
      JSON.stringify({ ...landing.first, captured_at: now }),
      parentDomainCookieOptions(FIRST_ATTR_EXPIRY_DAYS)
    );
  }
  if (landing.signup) {
    Cookies.set(
      LAST_ATTR_COOKIE,
      JSON.stringify({ ...landing.signup, captured_at: now }),
      parentDomainCookieOptions(LAST_ATTR_EXPIRY_DAYS)
    );
  }
};
